import { Component, HostListener } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { FACULTY_REFERRAL_CATEGORY, FacultyReferralFeedbackHistoryEntry, FacultyReferralFeedbackHistoryItem, FacultyReferralFormService, FacultyReferralOption, FacultyReferralSavePayload, MappedStudentSearchRow, StudentProfileInfoResponse, StudentSearchRowDto } from '../faculty-referral-form.service';
export type ReferralStudent = MappedStudentSearchRow;

export type ReferralLanguage = 'English' | 'Telugu';
interface ReferralQuestion {
  no: number;
  questionId: number;
  labelEn: string;
  labelTe: string;
}

interface ScaleLegendItem {
  value: number;
  labelEn: string;
  labelTe: string;
}
interface HistoryRankStyles {
  border: string;
  background: string;
  circle: string;
}

interface ReferralAnswer {
  questionId: number;
  optionId: number;
  optionRank: number;
  optionText: string;
  /** From feedbackDetails when updating; 0 for first-time save */
  feedbackId: number;
}
interface HistoryQuestionView {
  questionId: number;
  question: string;
  historyItem: FacultyReferralFeedbackHistoryItem;
}
@Component({
  selector: 'app-faculty-referral-form',
  templateUrl: './faculty-referral-form.component.html',
  styleUrls: ['./faculty-referral-form.component.scss']
})
export class FacultyReferralFormComponent {
  readonly categoryName = FACULTY_REFERRAL_CATEGORY;

  loading = false;
  listLoading = false;
  questionsLoading = false;
  errorMessage = '';
  submitting = false;
  submitted = false;
  showSubmitConfirm = false;
  showHistoryView = false;
  historyLoading = false;
  feedbackHistory: FacultyReferralFeedbackHistoryEntry[] = [];
  selectedHistoryDateIndex = 0;

  selectedStudent: ReferralStudent | null = null;

  searchAdmissionNo = '';
  language: ReferralLanguage = 'English';
  langDropdownOpen = false;

  readonly languageOptions: { value: ReferralLanguage; label: string }[] = [
    { value: 'English', label: 'English' },
    { value: 'Telugu', label: 'తెలుగు' }
  ];

  recentStudents: ReferralStudent[] = [];
  searchResults: ReferralStudent[] | null = null;

  questions: ReferralQuestion[] = [];
  profileLoading = false;
  profileError = '';
  profilePhotoFailed = false;
  dialogTitle = '';
  dialogMessage = '';
  showDialog = false;
  /** Student photo from getUploadImage (blob URL; JWT required). */
  studentPhotoObjectUrl: string | null = null;
  /** Legend labels from option names (ranks 1–3) on first question */
  scaleLegend: ScaleLegendItem[] = [
    { value: 1, labelEn: 'Mild', labelTe: 'తక్కువ' },
    { value: 2, labelEn: 'Moderate', labelTe: 'మధ్యస్థం' },
    { value: 3, labelEn: 'High', labelTe: 'ఎక్కువ' }
  ];

  private answers = new Map<number, ReferralAnswer>();
  private readonly historyOptionsByQuestionId = new Map<number, FacultyReferralOption[]>();
  private studentPhotoRevokeUrl: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly api: FacultyReferralFormService,
    private readonly spinner: NgxSpinnerService
  ) { }

  ngOnInit(): void {
    this.loadRecentStudents();
  }

  ngOnDestroy(): void {
    this.spinner.hide();
    this.revokeStudentPhotoObjectUrl();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get studentPhotoUrl(): string | null {
    return this.studentPhotoObjectUrl || this.selectedStudent?.photoUrl || null;
  }

  options$(questionId: number): Observable<FacultyReferralOption[]> {
    return this.api.getFacultyReferralOptionsCached(questionId);
  }

  get displayStudents(): ReferralStudent[] {
    if (this.searchResults !== null) return this.searchResults;
    return this.recentStudents;
  }

  get recentStudentsCount(): number {
    return this.recentStudents.length;
  }

  get answeredCount(): number {
    return this.answers.size;
  }

  get totalQuestions(): number {
    return this.questions.length;
  }

  get progressText(): string {
    return `${this.answeredCount}/${this.totalQuestions}`;
  }

  get progressPercent(): number {
    if (this.totalQuestions <= 0) return 0;
    return Math.round((this.answeredCount / this.totalQuestions) * 100);
  }

  get concernsRatedLabel(): string {
    return `${this.answeredCount} / ${this.totalQuestions} Concerns Rated`;
  }

  get completedLabel(): string {
    return `${this.answeredCount} of ${this.totalQuestions} completed`;
  }

  get studentSubline(): string {
    const type = String(this.selectedStudent?.boardingType ?? '').trim();
    const campus = String(this.selectedStudent?.campus ?? '').trim();
    if (type && campus) return `${type} · ${campus}`;
    return type || campus || '—';
  }

  get canSubmitEvaluation(): boolean {
    return this.totalQuestions > 0 && this.answeredCount > 0;
  }

  get evalHeadTitle(): string {
    return this.language === 'Telugu' ? 'మీ ఎంపిక చేయండి' : 'Make your choice';
  }

  get currentLang(): ReferralLanguage {
    return this.language;
  }

  get languageLabel(): string {
    return this.languageOptions.find((o) => o.value === this.language)?.label ?? this.language;
  }

  setLanguage(lang: ReferralLanguage): void {
    this.language = lang;
  }

  toggleLangDropdown(event: Event): void {
    event.stopPropagation();
    this.langDropdownOpen = !this.langDropdownOpen;
  }

  selectLanguage(lang: ReferralLanguage): void {
    this.setLanguage(lang);
    this.langDropdownOpen = false;
  }

  @HostListener('document:click')
  closeLangDropdown(): void {
    this.langDropdownOpen = false;
  }

  /** 1 = red, 2 = orange, 3 = green */
  scaleRankClass(rank: number): string {
    const r = Number(rank);
    if (r === 1) return 'frf-scale--rank-1';
    if (r === 2) return 'frf-scale--rank-2';
    if (r === 3) return 'frf-scale--rank-3';
    return '';
  }

  get historyFeedbackCountLabel(): string {
    const count = this.feedbackHistory.length;
    if (count <= 0) return '';
    return `Last ${count} feedback`;
  }

  formatHistoryDate(entry: FacultyReferralFeedbackHistoryEntry): string {
    return String(entry.feedbackDate ?? entry.feedBackDate ?? '').trim();
  }

  selectHistoryDate(index: number): void {
    if (index < 0 || index >= this.feedbackHistory.length) return;
    this.selectedHistoryDateIndex = index;
  }

  getSelectedHistoryQuestions(): HistoryQuestionView[] {
    const entry = this.feedbackHistory[this.selectedHistoryDateIndex];
    const items = entry?.feedBackHistory;
    if (!Array.isArray(items) || !items.length) return [];

    return items.map((item) => {
      const questionId = Number(item.questionId);
      const q = this.questions.find((row) => row.questionId === questionId);
      return {
        questionId,
        question: q ? this.questionLabel(q) : `Question ${questionId}`,
        historyItem: item
      };
    });
  }

  getSelectedHistoryOptions(q: HistoryQuestionView): FacultyReferralOption[] {
    const opts = this.historyOptionsByQuestionId.get(q.questionId) ?? [];
    const selected = opts.find((o) => Number(o.optionId) === Number(q.historyItem.optionId));
    if (selected) return [selected];

    const fallbackText = String(q.historyItem.feedbackOptionText ?? '').trim();
    return [
      {
        optionId: Number(q.historyItem.optionId),
        optionName: fallbackText || String(q.historyItem.optionId),
        optionRank: 0
      }
    ];
  }

  getHistoryOptionRank(opt: FacultyReferralOption): number {
    return Number(opt.optionRank ?? 0);
  }

  getHistoryOptionLabel(opt: FacultyReferralOption): string {
    return this.optionLabel(opt);
  }

  getHistoryRankStyles(rank: number): HistoryRankStyles {
    const r = Number(rank);
    if (r === 1) {
      return { border: '#f5c4cc', background: '#fcedf0', circle: '#e52b40' };
    }
    if (r === 2) {
      return { border: '#fadea3', background: '#fff7e5', circle: '#f29900' };
    }
    if (r === 3) {
      return { border: '#c7ede0', background: '#e8faf5', circle: '#12ad80' };
    }
    return { border: '#e5e7eb', background: '#f9fafb', circle: '#6b7280' };
  }

  questionLabel(q: ReferralQuestion): string {
    if (this.language === 'Telugu') {
      return q.labelTe || q.labelEn;
    }
    return q.labelEn;
  }

  legendLabel(item: ScaleLegendItem): string {
    if (this.language === 'Telugu') {
      return item.labelTe || item.labelEn;
    }
    return item.labelEn;
  }

  legendSubtext(rank: number): string {
    const r = Number(rank);
    if (this.language === 'Telugu') {
      if (r === 1) return 'తక్కువ / అప్పుడప్పుడు';
      if (r === 2) return 'పునరావృతం / గమనించదగిన ప్రభావం';
      if (r === 3) return 'నిరంతరం / తక్షణ సంరక్షణ అవసరం';
      return '';
    }
    if (r === 1) return 'Slight / Occasional concern';
    if (r === 2) return 'Recurring / Noticeable impact';
    if (r === 3) return 'Persistent / Needs urgent care';
    return '';
  }

  scaleOptionButtonLabel(option: FacultyReferralOption): string {
    const rank = Number(option.optionRank);
    return `${rank} ${this.optionLabel(option)}`;
  }

  clearAllAnswers(): void {
    this.answers.clear();
    this.errorMessage = '';
  }

  optionLabel(option: FacultyReferralOption): string {
    const en = String(option.optionName ?? '').trim();
    const te = String(option.teluguOptionName ?? '').trim();
    if (this.language === 'Telugu') {
      return te || en;
    }
    return en;
  }

  isSelected(questionId: number, optionId: number): boolean {
    return this.answers.get(questionId)?.optionId === optionId;
  }

  setAnswer(questionId: number, option: FacultyReferralOption): void {
    const existing = this.answers.get(questionId);
    this.answers.set(questionId, {
      questionId,
      optionId: Number(option.optionId),
      optionRank: Number(option.optionRank),
      optionText: this.optionLabel(option),
      feedbackId: existing?.feedbackId ?? 0
    });
  }

  onSearchEnter(event: Event): void {
    event.preventDefault();
    this.onSearchClick();
  }

  /** When search field is cleared, show recent students again. */
  onSearchAdmissionNoChange(value: string): void {
    if (!String(value ?? '').trim()) {
      this.searchResults = null;
      this.listLoading = false;
    }
  }

  /** Search button / Enter — always calls API (not blocked by distinctUntilChanged). */
  onSearchClick(): void {
    const term = this.searchAdmissionNo.trim();
    if (!term) {
      this.listLoading = false;
      this.searchResults = null;
      return;
    }
    this.executeSearch(term);
  }

  private executeSearch(term: string): void {
    this.listLoading = true;
    this.errorMessage = '';
    this.spinner.show();
    this.api
      .searchCollegeStudents({ searchKey: term, pageCount: 0, businessType: 'COLLEGE' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.listLoading = false;
          this.spinner.hide();
          if (!this.searchAdmissionNo.trim()) {
            this.searchResults = null;
            return;
          }
          this.searchResults = (rows ?? []).map((r) => this.api.mapRowToStudent(r));
        },
        error: () => {
          this.listLoading = false;
          this.spinner.hide();
          this.errorMessage = 'Student search failed. Please try again.';
          this.searchResults = [];
        }
      });
  }

  selectStudent(student: ReferralStudent): void {
    if (!student.admissionNo || !student.name) {
      this.errorMessage = 'Invalid student record.';
      return;
    }

    this.listLoading = true;
    this.errorMessage = '';
    this.spinner.show();

    this.api.searchCollegeStudentByRecord({
      admissionNo: student.admissionNo,
      name: student.name,
      parentName: student.parentName
    }).subscribe({
      next: (rows) => {
        const resolved = this.resolveStudentFromSearchRows(student, rows);
        this.saveCacheAndOpenEvaluation(resolved);
      },
      error: () => {
        this.saveCacheAndOpenEvaluation(student);
      }
    });
  }

  /** Match search API row to the tapped student (fresh admNo / stdUniqueRefId). */
  private resolveStudentFromSearchRows(
    picked: ReferralStudent,
    rows: StudentSearchRowDto[]
  ): ReferralStudent {
    const list = (rows ?? []).map((r) => this.api.mapRowToStudent(r));
    if (!list.length) return picked;

    const adm = picked.admissionNo.trim();
    const name = picked.name.trim();
    return (
      list.find((s) => s.admissionNo === adm) ??
      list.find((s) => s.name === name) ??
      list.find(
        (s) =>
          s.stdUniqueRefId &&
          picked.stdUniqueRefId &&
          s.stdUniqueRefId === picked.stdUniqueRefId
      ) ??
      list[0]
    );
  }

  private saveCacheAndOpenEvaluation(student: ReferralStudent): void {
    this.api.saveSearchCache(this.api.buildSaveCacheParams(student)).subscribe({
      next: () => this.openEvaluation(student),
      error: (err) => this.handleSaveCacheError(err, student)
    });
  }

  private handleSaveCacheError(err: unknown, student: ReferralStudent): void {
    this.listLoading = false;
    const body = (err as { body?: unknown })?.body;
    const msg = String((err as Error)?.message ?? '').toLowerCase();
    const status = Number((err as { status?: number })?.status ?? 0);

    if (
      status === 403 ||
      status === 401 ||
      msg.includes('access denied') ||
      (body &&
        typeof body === 'object' &&
        String((body as { error?: string }).error ?? '')
          .toLowerCase()
          .includes('denied'))
    ) {
      this.errorMessage =
        'Recent students could not be saved (403). Evaluation will still open — ' +
        'compare the saveCache URL in Network tab with Swagger.';
      this.openEvaluation(student);
      return;
    }

    this.openEvaluation(student);
  }

  back(): void {
    if (this.selectedStudent) {
      this.selectedStudent = null;
      this.answers.clear();
      this.questions = [];
      this.searchAdmissionNo = '',
        this.errorMessage = '';
      this.showHistoryView = false;
      this.historyLoading = false;
      this.feedbackHistory = [];
      this.selectedHistoryDateIndex = 0;
      this.historyOptionsByQuestionId.clear();
      this.revokeStudentPhotoObjectUrl();
      this.searchResults = null;
      this.loadRecentStudents();
      return;
    }
    // this.location.back();
  }

  onCheckHistory(): void {
    if (this.showHistoryView) {
      this.showHistoryView = false;
      this.feedbackHistory = [];
      this.selectedHistoryDateIndex = 0;
      this.historyOptionsByQuestionId.clear();
      this.errorMessage = '';
      return;
    }

    const admNo = this.selectedStudent?.admNoToken?.trim();
    if (!admNo) {
      this.errorMessage = 'Student not found.';
      return;
    }

    this.historyLoading = true;
    this.errorMessage = '';
    this.spinner.show();

    this.api.getFacultyReferralFeedbackHistory(admNo, this.categoryName).subscribe({
      next: (list) => {
        this.historyLoading = false;
        this.spinner.hide();
        if (!list.length) {
          this.errorMessage = 'No feedback history found for this student.';
          return;
        }
        this.feedbackHistory = list;
        this.selectedHistoryDateIndex = 0;
        this.showHistoryView = true;
        this.preloadHistoryOptions();
      },
      error: () => {
        this.historyLoading = false;
        this.spinner.hide();
        this.errorMessage = 'Could not load feedback history.';
      }
    });
  }

  submit(): void {
    this.errorMessage = '';
    if (!this.canSubmitEvaluation) {
      this.errorMessage = `Please rate all criteria (${this.answeredCount}/${this.totalQuestions}).`;
      return;
    }
    this.showSubmitConfirm = true;
  }

  cancelSubmitConfirm(): void {
    this.showSubmitConfirm = false;
  }

  confirmSubmit(): void {
    this.showSubmitConfirm = false;
    if (this.submitting) return;

    const student = this.selectedStudent;
    if (!student?.admissionNo?.trim()) {
      this.errorMessage = 'Student admission number is missing.';
      return;
    }

    const payload = this.buildSavePayload(student);
    this.errorMessage = '';
    this.submitting = true;
    this.spinner.show();

    this.api.saveFacultyReferralDetails(payload).subscribe({
      next: () => {
        this.submitting = false;
        this.spinner.hide();
        this.submitted = true;
        this.dialogTitle = 'Thank you';
        this.dialogMessage = 'Your evaluation has been submitted successfully.';
          this.showDialog = true;

      },
      error: (err: any) => {
        this.submitting = false;
        this.spinner.hide();
        const status = Number(err?.status ?? 0);
        if (status === 204 || status === 200) {
          this.submitted = true;
          return;
        }
        this.dialogTitle = 'Error';
        this.dialogMessage =
          err?.error?.message ||
          err?.message ||
          'Save failed. Please try again.';
            this.showDialog = true;
      }
    });
  }

  /** POST saveStdFeedbackDetails — flat questionAndOption (Swagger shape) */
  private buildSavePayload(student: ReferralStudent): FacultyReferralSavePayload {
    return {
      admNO: String(student.admissionNo).trim(),
      empId: this.api.getStoredEmpId(),
      feedBackType: 'EMPLOYEE_FEEDBACK',
      category: this.categoryName,
      sectionId: Number(student.sectionId ?? 0),
      questionAndOption: Array.from(this.answers.values()).map((a) => ({
        questionId: Number(a.questionId),
        optionId: Number(a.optionId),
        feedbackOptionText: '',
        feedbackPopUpText: '',
        feedbackId: Number(a.feedbackId ?? 0) || 0
      }))
    };
  }

  confirmationBack(): void {
    this.submitted = false;
    this.showDialog=false
    this.selectedStudent = null;
    this.revokeStudentPhotoObjectUrl();
    this.answers.clear();
    this.questions = [];
    this.searchAdmissionNo = '';
    this.searchResults = null;
    this.loadRecentStudents();
  }

  private openEvaluation(student: ReferralStudent): void {
    this.listLoading = false;
    this.selectedStudent = student;
    this.profileLoading = true;
    this.language = 'English';
    this.langDropdownOpen = false;
    this.profileError = '';
    this.profilePhotoFailed = false;
    this.revokeStudentPhotoObjectUrl();
    this.answers.clear();
    this.errorMessage = '';
    this.submitted = false;
    this.showHistoryView = false;
    this.historyLoading = false;
    this.feedbackHistory = [];
    this.selectedHistoryDateIndex = 0;
    this.historyOptionsByQuestionId.clear();
    this.loadQuestions();
    this.loadSavedFeedback(false);

    const admNoToken = student.admNoToken?.trim();
    if (admNoToken) {
      this.loadStudentPhoto(admNoToken);
      this.api.getStudentProfileInfo(admNoToken).pipe(takeUntil(this.destroy$)).subscribe({
        next: (profile) => {
          this.profileLoading = false;
          this.applyStudentProfile(profile);
        },
        error: () => {
          this.profileLoading = false;
          this.profileError = 'Could not load student profile.';
        }
      });
    } else {
      this.profileLoading = false;
    }
  }

  private applyStudentProfile(profile: StudentProfileInfoResponse): void {
    if (!this.selectedStudent) return;

    const nextName = String(
      profile.studentName ?? profile.name ?? this.selectedStudent.name ?? ''
    ).trim();
    const nextAdmissionNo = String(
      profile.admissionNo ?? profile.admNo ?? this.selectedStudent.admissionNo ?? ''
    ).trim();
    const nextCampus = String(
      profile.campusName ?? profile.campus ?? this.selectedStudent.campus ?? ''
    ).trim();
    const nextType = String(
      profile.studentType ?? profile.boardingType ?? this.selectedStudent.boardingType ?? ''
    ).trim();
    const nextParentName = String(
      profile['parentName'] ??
      profile['fatherName'] ??
      (profile as Record<string, unknown>)['parentsName'] ??
      this.selectedStudent.parentName ??
      ''
    ).trim();

    if (nextName) this.selectedStudent.name = nextName;
    if (nextAdmissionNo) this.selectedStudent.admissionNo = nextAdmissionNo;
    if (nextCampus) this.selectedStudent.campus = nextCampus;
    if (nextType) this.selectedStudent.boardingType = nextType;
    if (nextParentName) this.selectedStudent.parentName = nextParentName;

    const sectionId = this.readSectionId(profile);
    if (sectionId > 0) {
      this.selectedStudent.sectionId = sectionId;
    }

    const photo = this.readProfilePhotoUrl(profile);
    if (photo && !this.studentPhotoObjectUrl) {
      this.selectedStudent.photoUrl = photo;
      this.profilePhotoFailed = false;
    }
  }

  private loadStudentPhoto(admNoToken: string): void {
    this.revokeStudentPhotoObjectUrl();
    this.profilePhotoFailed = false;

    this.api
      .getStudentUploadImageBlob(admNoToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          if (!blob?.size) return;
          if (blob.type && !blob.type.startsWith('image/')) return;
          const url = URL.createObjectURL(blob);
          this.studentPhotoRevokeUrl = url;
          this.studentPhotoObjectUrl = url;
          if (this.selectedStudent) {
            this.selectedStudent.photoUrl = url;
          }
        },
        error: () => {
          this.profilePhotoFailed = true;
        }
      });
  }

  private revokeStudentPhotoObjectUrl(): void {
    if (this.studentPhotoRevokeUrl) {
      try {
        URL.revokeObjectURL(this.studentPhotoRevokeUrl);
      } catch {
        /* ignore */
      }
      this.studentPhotoRevokeUrl = null;
    }
    this.studentPhotoObjectUrl = null;
  }

  onProfilePhotoError(): void {
    this.profilePhotoFailed = true;
  }

  private readSectionId(profile: StudentProfileInfoResponse): number {
    const raw = profile as Record<string, unknown>;
    const v =
      profile.sectionId ??
      profile.secId ??
      raw['sectionID'] ??
      raw['secID'] ??
      raw['section_id'];
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
  }

  private readProfilePhotoUrl(profile: StudentProfileInfoResponse): string {
    const raw = profile as Record<string, unknown>;
    const v =
      profile.photoUrl ??
      profile.imageUrl ??
      raw['studentPhoto'] ??
      raw['studentImage'] ??
      raw['profileImage'] ??
      raw['photo'] ??
      raw['imagePath'];
    return String(v ?? '').trim();
  }

  private loadSavedFeedback(showHistoryMessage: boolean): void {
    const admNo = this.selectedStudent?.admNoToken?.trim();
    if (!admNo) return;

    this.api.getFacultyReferralFeedbackDetails(admNo, this.categoryName).subscribe({
      next: (res) => {
        const applied = this.applyFeedbackDetails(res);
        if (showHistoryMessage) {
          this.errorMessage = applied
            ? 'Previous ratings loaded.'
            : 'No previous referral found for this student.';
        }
      },
      error: () => {
        if (showHistoryMessage) {
          this.errorMessage = 'Could not load referral history.';
        }
      }
    });
  }

  private applyFeedbackDetails(res: unknown): boolean {
    const root = this.unwrapFeedbackDetails(res);
    if (!root) return false;

    const sectionFromHistory = Number(root['sectionId'] ?? 0);
    if (this.selectedStudent && Number.isFinite(sectionFromHistory) && sectionFromHistory > 0) {
      this.selectedStudent.sectionId = sectionFromHistory;
    }

    const list = root['questionAndOption'];
    if (!Array.isArray(list) || !list.length) return false;

    for (const item of list) {
      const row = item as Record<string, unknown>;
      const questionId = Number(row['questionId']);
      if (!Number.isFinite(questionId)) continue;

      const optionId = Number(row['optionId']);
      if (!Number.isFinite(optionId)) continue;

      const feedbackId = Number(row['feedbackId'] ?? 0);
      this.answers.set(questionId, {
        questionId,
        optionId,
        optionRank: Number(row['optionRank'] ?? 0),
        optionText: String(row['feedbackOptionText'] ?? row['optionName'] ?? '').trim(),
        feedbackId: Number.isFinite(feedbackId) && feedbackId > 0 ? Math.trunc(feedbackId) : 0
      });
    }
    return this.answers.size > 0;
  }

  private unwrapFeedbackDetails(res: unknown): Record<string, unknown> | null {
    if (!res || typeof res !== 'object') return null;
    const o = res as Record<string, unknown>;
    const nested = o['data'] ?? o['result'] ?? o['feedbackDetails'];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as Record<string, unknown>;
    }
    return o;
  }

  private preloadHistoryOptions(): void {
    const questionIds = new Set<number>();
    for (const entry of this.feedbackHistory) {
      for (const item of entry.feedBackHistory ?? []) {
        const id = Number(item.questionId);
        if (Number.isFinite(id) && id > 0) questionIds.add(id);
      }
    }

    for (const id of questionIds) {
      this.api
        .getFacultyReferralOptionsCached(id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (opts) => {
            this.historyOptionsByQuestionId.set(id, opts ?? []);
          }
        });
    }
  }

  private loadQuestions(): void {
    this.questionsLoading = true;
    this.errorMessage = '';
    this.questions = [];

    this.api.getFacultyReferralQuestions(this.categoryName).subscribe({
      next: (list) => {
        const rows = Array.isArray(list) ? list : [];
        this.questions = rows.map((q, idx) => {
          const title = String(q.questionTitle ?? '').trim();
          const bodyEn = this.stripHtml(String(q.question ?? ''));
          const bodyTe = this.stripHtml(String(q.teluguQuestion ?? ''));
          const labelEn = title || bodyEn || `Question ${idx + 1}`;
          const labelTe = bodyTe || title || labelEn;
          return {
            no: idx + 1,
            questionId: Number(q.questionId),
            labelEn,
            labelTe
          };
        });
        this.questionsLoading = false;
        this.spinner.hide();
        this.loadScaleLegendFromFirstQuestion();
      },
      error: () => {
        this.questionsLoading = false;
        this.spinner.hide();
        this.errorMessage = 'Could not load questions. Please try again.';
        this.questions = [];
      }
    });
  }

  private loadScaleLegendFromFirstQuestion(): void {
    const firstId = this.questions[0]?.questionId;
    if (!firstId) return;

    this.api.getFacultyReferralOptions(firstId).subscribe({
      next: (opts) => {
        const sorted = [...(opts ?? [])].sort((a, b) => Number(a.optionRank) - Number(b.optionRank));
        if (!sorted.length) return;
        this.scaleLegend = sorted.map((o) => {
          const labelEn = String(o.optionName ?? '').trim() || String(o.optionRank);
          const labelTe =
            String(o.teluguOptionName ?? '').trim() || labelEn;
          return {
            value: Number(o.optionRank),
            labelEn,
            labelTe
          };
        });
      }
    });
  }

  private stripHtml(html: string): string {
    return String(html ?? '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private loadRecentStudents(): void {
    this.loading = true;
    this.errorMessage = '';
    this.spinner.show();
    this.api.getSearchCacheDetails().subscribe({
      next: (rows) => {
        this.loading = false;
        this.spinner.hide();
        this.recentStudents = (rows ?? []).map((r) => this.api.mapRowToStudent(r));
      },
      error: () => {
        this.loading = false;
        this.spinner.hide();
        this.recentStudents = [];
        this.errorMessage = 'Could not load recent students.';
      }
    });
  }

  studentListInitial(student: ReferralStudent): string {
    const name = String(student.name ?? '').trim();
    return name ? name.charAt(0).toUpperCase() : '?';
  }

  orientationBadgeLabel(student: ReferralStudent): string {
    const value = String(
      student.studentOrientation ?? student.boardingType ?? ''
    ).trim();
    const lower = value.toLowerCase();
    if (lower.includes('day')) return 'Day';
    if (lower.includes('res')) return 'Res';
    return '';
  }

  trackByAdmission = (_: number, s: ReferralStudent): string =>
    s.stdUniqueRefId || s.admissionNo || s.name;
  trackByQuestionId = (_: number, q: ReferralQuestion): number => q.questionId;
  trackByOptionId = (_: number, o: FacultyReferralOption): number => o.optionId;
}
